import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";
import { useSocketContext } from "src/context/SocketContext";
import { useSelector } from "react-redux";
import authConfig from "src/configs/auth";
import { toast } from "react-hot-toast";
import { ApiEndPoints } from "src/network/endpoints";
import { axiosInstance } from "src/network/adapter";
import MessageTimer from "../MessageTimer";

/**
 * PaymentRequestSplitEvenly Component
 * 
 * Handles payment_request_split_evenly message type
 * - Shows waiting status for the initiator
 * - Shows accept/reject buttons for other users who need to respond
 */
const PaymentRequestSplitEvenly = ({ 
  message, 
  isOwn, 
  currentUser, 
  storedUserId 
}) => {
  const theme = useTheme();
  const { socket } = useSocketContext();
  const [isResponding, setIsResponding] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [userHasResponded, setUserHasResponded] = useState(false);
  
  // Get current user ID — normalise to string for reliable comparison with MongoDB ObjectIds
  const currentUserId = (currentUser?.userId ?? storedUserId)?.toString();
  
  // Get order and payment flow data
  const orderId = message?.orderId;
  const paymentFlow = orderId?.paymentFlow;
  // Normalise initiatedBy to string to match against currentUserId
  const initiatedBy = paymentFlow?.initiatedBy?._id?.toString() ?? paymentFlow?.initiatedBy?.toString();
  const responses = paymentFlow?.request?.responses || [];
  const paymentFlowStatus = paymentFlow?.status;
  const totalAmount = message?.amount || 0;
  
  // Check if current user is the initiator
  const isInitiator = !!initiatedBy && initiatedBy === currentUserId;

  // Currency symbol — stored on the message by the server, fallback to $
  const currencySymbol = message?.currencySymbol || "$";
  
  // Find current user's response — normalise response.userId to string before comparison
  const userResponse = responses.find(
    response => (response.userId?._id?.toString() ?? response.userId?.toString()) === currentUserId
  );
  const userResponseStatus = userResponse?.response || "pending";
  
  // Check if user has already responded (from server data)
  const hasUserResponded = userResponseStatus !== "pending";
  
  // Timer logic - show timer immediately, it counts down from 30 seconds
  useEffect(() => {
    if (message?.createdAt) {
      if (isInitiator || (!isInitiator && !hasUserResponded)) {
        setTimerActive(true);
      }
    }
  }, [message?.createdAt, isInitiator, hasUserResponded]);
  
  // Sync local responded state when server data confirms response
  useEffect(() => {
    if (hasUserResponded) {
      setUserHasResponded(true);
    }
  }, [hasUserResponded]);
  
  // Calculate response statistics
  const acceptedResponses = responses.filter(r => r.response === "accepted" || r.response === "approved").length;
  const rejectedResponses = responses.filter(r => r.response === "rejected").length;
  const pendingResponses = responses.filter(r => r.response === "pending").length;

  // Total participants = non-initiator users (stored in message.initiationFor) + the initiator.
  // responses excludes the initiator, so responses.length alone under-counts by 1.
  // initiationFor is stored directly on the Message document and is always reliably populated.
  const nonInitiatorCount = message?.initiationFor?.length ?? responses.length;
  const totalParticipants = nonInitiatorCount + 1; // +1 for the initiator
  const amountPerPerson = totalParticipants > 0 ? totalAmount / totalParticipants : 0;
  
  // Handle accept response
  const handleAccept = async () => {
    if (isResponding || userHasResponded) return;
    
    setIsResponding(true);
    try {
      const res = await axiosInstance.post(
        ApiEndPoints.ORDER.paymentResponse(orderId._id),
        { response: "approved" }
      );
      
      if (res.data.status === "success") {
        toast.success("You accepted the split bill request");
        setUserHasResponded(true);
        
        if (socket) {
          socket.emit("payment_split_response", {
            orderId: orderId._id,
            userId: currentUserId,
            response: "accepted",
            amount: amountPerPerson
          });
        }
      } else {
        throw new Error(res.data.message || "Failed to accept request");
      }
    } catch (error) {
      console.error("Error accepting split request:", error);
      toast.error(error.response?.data?.message || "Failed to accept request. Please try again.");
    } finally {
      setIsResponding(false);
    }
  };
  
  // Handle reject response
  const handleReject = async () => {
    if (isResponding || userHasResponded) return;
    
    setIsResponding(true);
    try {
      const res = await axiosInstance.post(
        ApiEndPoints.ORDER.paymentResponse(orderId._id),
        { response: "rejected" }
      );
      
      if (res.data.status === "success") {
        toast.success("You rejected the split bill request");
        setUserHasResponded(true);
        
        // Also emit socket event for real-time updates
        if (socket) {
          socket.emit("payment_split_response", {
            orderId: orderId._id,
            userId: currentUserId,
            response: "rejected",
            amount: 0
          });
        }
      } else {
        throw new Error(res.data.message || "Failed to reject request");
      }
    } catch (error) {
      console.error("Error rejecting split request:", error);
      toast.error(error.response?.data?.message || "Failed to reject request. Please try again.");
    } finally {
      setIsResponding(false);
    }
  };
  
  // Render initiator view (waiting for responses)
  if (isInitiator) {
    return (
      <MessageBubbleContainer isOwn={isOwn}>
        <MessageContent isOwn={isOwn} isPayment={true}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TimeIcon sx={{ color: isOwn ? "#FFFFFF" : "#7C3AED" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isOwn ? "#FFFFFF" : "#7C3AED",
                  textTransform: "lowercase",
                }}
              >
                waiting for responses
              </Typography>
            </Box>
            
            {/* Message */}
            <Typography
              variant="body1"
              sx={{
                color: isOwn ? "#FFFFFF" : "#374151",
                textTransform: "lowercase",
              }}
            >
              {message.message || message.text}
            </Typography>
            
            {/* Timer Display */}
            {timerActive && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TimeIcon sx={{ color: isOwn ? "#FFFFFF" : "#6B7280", fontSize: 16 }} />
                <MessageTimer
                  createdAt={message?.createdAt}
                  onReachZero={() => {
                    // Timer expired - could show expired message
                    console.log("Timer expired for split bill request");
                  }}
                  onAlreadyReachZero={() => {
                    // Already expired when component loaded
                    console.log("Timer already expired for split bill request");
                  }}
                />
              </Box>
            )}
            
            {/* Response Statistics */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label={`${acceptedResponses} accepted`}
                color="success"
                size="small"
                sx={{ textTransform: "lowercase" }}
              />
              <Chip
                label={`${rejectedResponses} rejected`}
                color="error"
                size="small"
                sx={{ textTransform: "lowercase" }}
              />
              <Chip
                label={`${pendingResponses} pending`}
                color="warning"
                size="small"
                sx={{ textTransform: "lowercase" }}
              />
            </Box>
            
            {/* Amount per person */}
            <Typography
              variant="body2"
              sx={{
                color: isOwn ? "#FFFFFF" : "#6B7280",
                textTransform: "lowercase",
                fontWeight: 500,
              }}
            >
              amount per person: {currencySymbol}{amountPerPerson.toFixed(2)}
            </Typography>
          </Box>
        </MessageContent>
      </MessageBubbleContainer>
    );
  }
  
  // Render responder view (accept/reject buttons)
  return (
    <MessageBubbleContainer isOwn={isOwn}>
      <MessageContent isOwn={isOwn} isPayment={true}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isOwn ? "#FFFFFF" : "#7C3AED",
                textTransform: "lowercase",
              }}
            >
              split bill request
            </Typography>
          </Box>
          
          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              color: isOwn ? "#FFFFFF" : "#374151",
              textTransform: "lowercase",
            }}
          >
            {message.message || message.text}
          </Typography>
          
          {/* Amount per person */}
          <Typography
            variant="body2"
            sx={{
              color: isOwn ? "#FFFFFF" : "#6B7280",
              textTransform: "lowercase",
              fontWeight: 500,
            }}
          >
            your share: {currencySymbol}{amountPerPerson.toFixed(2)}
          </Typography>
          
          {/* Timer Display */}
          {timerActive && !userHasResponded && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <TimeIcon sx={{ color: isOwn ? "#FFFFFF" : "#6B7280", fontSize: 16 }} />
              <MessageTimer
                createdAt={message?.createdAt}
                onReachZero={() => {
                  // Timer expired - could show expired message or disable buttons
                  console.log("Timer expired for split bill request");
                }}
                onAlreadyReachZero={() => {
                  // Already expired when component loaded
                  console.log("Timer already expired for split bill request");
                }}
              />
            </Box>
          )}
          
          {/* Action Buttons — hide if user responded locally OR if server confirms response */}
          {!userHasResponded && userResponseStatus === "pending" ? (
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<AcceptIcon />}
                onClick={handleAccept}
                disabled={isResponding}
                sx={{
                  textTransform: "lowercase",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {isResponding ? "accepting..." : "accept"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={handleReject}
                disabled={isResponding}
                sx={{
                  textTransform: "lowercase",
                  fontWeight: 600,
                  px: 3,
                  borderColor: isOwn ? "#FFFFFF" : "#ef4444",
                  color: isOwn ? "#FFFFFF" : "#ef4444",
                  "&:hover": {
                    borderColor: isOwn ? "#FFFFFF" : "#ef4444",
                    backgroundColor: isOwn ? "rgba(255,255,255,0.1)" : "rgba(239,68,68,0.1)",
                  },
                }}
              >
                {isResponding ? "rejecting..." : "reject"}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              {(userResponseStatus === "accepted" || userResponseStatus === "approved") ? (
                <>
                  <AcceptIcon color="success" />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isOwn ? "#FFFFFF" : "#10b981",
                      textTransform: "lowercase",
                      fontWeight: 600,
                    }}
                  >
                    you accepted this request
                  </Typography>
                </>
              ) : userResponseStatus === "rejected" ? (
                <>
                  <RejectIcon color="error" />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isOwn ? "#FFFFFF" : "#ef4444",
                      textTransform: "lowercase",
                      fontWeight: 600,
                    }}
                  >
                    you rejected this request
                  </Typography>
                </>
              ) : null}
            </Box>
          )}
        </Box>
      </MessageContent>
    </MessageBubbleContainer>
  );
};

export default PaymentRequestSplitEvenly;
